import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { broadcastLogin, broadcastLogout } from "@/lib/authSync";

const AdminCtx = createContext(null);

export function AdminProvider({ children }) {
  const [admin, setAdmin] = useState(() => {
    try { return JSON.parse(localStorage.getItem("rtg_admin") || "null"); } catch { return null; }
  });
  const [loading, setLoading] = useState(false);

  // v2.7.63+E46.5 · Sync admin session state across tabs so opening a
  // 2nd/3rd/4th monitor window inherits the token seamlessly.
  useEffect(() => {
    const handleStorage = (ev) => {
      if (ev.key === "rtg_admin_token") {
        if (ev.newValue) {
          try { setAdmin(JSON.parse(localStorage.getItem("rtg_admin") || "null")); } catch (_e) { /* noop */ }
        } else {
          setAdmin(null);
        }
      }
    };
    window.addEventListener("storage", handleStorage);
    // v2.7.65+E151 · Same-tab hook for BroadcastChannel-delivered auth
    // updates. Storage events don't fire for own-tab writes.
    const handleSameTab = (ev) => {
      const d = ev?.detail || {};
      if (d.type === "login") {
        try { setAdmin(JSON.parse(localStorage.getItem("rtg_admin") || "null")); } catch (_e) { /* noop */ }
      } else if (d.type === "logout") {
        setAdmin(null);
      }
    };
    window.addEventListener("rtg-auth-updated:admin", handleSameTab);
    return () => {
      window.removeEventListener("storage", handleStorage);
      window.removeEventListener("rtg-auth-updated:admin", handleSameTab);
    };
  }, []);

  const persist = useCallback((data) => {
    localStorage.setItem("rtg_admin_token", data.access_token);
    localStorage.setItem("rtg_admin", JSON.stringify(data.user));
    setAdmin(data.user);
    broadcastLogin("admin", { token: data.access_token, user: data.user });
  }, []);

  const login = useCallback(async (username, password, remember = false) => {
    setLoading(true);
    try {
      const { data } = await api.post("/admin/login", { username, password, remember: !!remember });
      // v2.6.7 batch H · if the server replies with a 2FA challenge instead
      // of an access_token, return the pending token to the caller so it can
      // render the 2FA prompt. AdminContext stays signed-out until verify.
      if (data?.requires_2fa) return { requires_2fa: true, pending_token: data.pending_token };
      persist(data);
      return data;
    } finally { setLoading(false); }
  }, [persist]);

  const verify2fa = useCallback(async (pending_token, code) => {
    setLoading(true);
    try {
      const { data } = await api.post("/admin/login/2fa-verify", { pending_token, code });
      persist(data);
      return data;
    } finally { setLoading(false); }
  }, [persist]);

  const loginPin = useCallback(async (pin) => {
    setLoading(true);
    try {
      const { data } = await api.post("/admin/login-pin", { pin });
      if (data?.requires_2fa) return { requires_2fa: true, pending_token: data.pending_token };
      persist(data);
      return data;
    } finally { setLoading(false); }
  }, [persist]);

  const logout = useCallback(() => {
    // v2.7.63+E48.1 · clear session-start keys so the next operator on
    // this device starts a fresh session timer on the OperatorBadge.
    Object.keys(localStorage)
      .filter((k) => k.startsWith("rtg_session_start_"))
      .forEach((k) => localStorage.removeItem(k));
    localStorage.removeItem("rtg_admin_token");
    localStorage.removeItem("rtg_admin");
    setAdmin(null);
    broadcastLogout("admin");
  }, []);

  const value = useMemo(
    () => ({ admin, loading, login, loginPin, verify2fa, logout }),
    [admin, loading, login, loginPin, verify2fa, logout],
  );

  return <AdminCtx.Provider value={value}>{children}</AdminCtx.Provider>;
}

export const useAdmin = () => useContext(AdminCtx);
